import React, { useEffect, useRef, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress as Bar } from '../components/ui/progress';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Loader2, CheckCircle2, ListChecks, Trophy, Download, Sparkles, Users, Share2, Send, Target, History, Medal, CalendarDays, Flag, Pencil, Gauge, Crown } from 'lucide-react';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import { routinesAPI, mediaUrl } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const PROFILE_LABEL = { client: 'Client', distributeur: 'Distributeur' };
const DAYS_FR = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
const SHARE_TEXT = "J'ai validé toutes mes routines du jour sur Body Fit Challenge 21 💪🔥 #BodyFitChallenge21";
const GOALS_SHARE_TEXT = "J'ai atteint tous mes objectifs de la semaine sur Body Fit Challenge 21 💪🔥 #BodyFitChallenge21";
const RANK_COLOR = ['text-amber-400', 'text-neutral-300', 'text-orange-600'];
const fmtWeek = (iso) => { const d = new Date(iso + 'T00:00:00'); return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }); };

const MiniBoard = ({ title, icon: Icon, rows, field }) => (
  <div data-testid={`goals-board-${field}`}>
    <h4 className="text-sm font-bold text-white flex items-center gap-1.5 mb-2"><Icon className="w-4 h-4 text-orange-400" />{title}</h4>
    {rows.length === 0 ? (
      <p className="text-xs text-neutral-500 py-2">Personne n'a encore marqué de points cette semaine.</p>
    ) : (
      <div className="space-y-1.5">
        {rows.map((r) => (
          <div key={r.user_id} data-testid={`goals-rank-${field}-${r.rank}`} className={`flex items-center gap-2.5 p-2 rounded-xl ${r.is_me ? 'bg-orange-500/15 border border-orange-500/30' : 'bg-white/5'}`}>
            <span className={`w-5 text-center font-extrabold text-sm ${RANK_COLOR[r.rank - 1] || 'text-neutral-500'}`}>{r.rank}</span>
            <Avatar className="w-7 h-7"><AvatarImage src={mediaUrl(r.avatar)} /><AvatarFallback className="bg-orange-500 text-white text-[10px]">{r.name?.charAt(0)}</AvatarFallback></Avatar>
            <span className="flex-1 min-w-0 truncate text-sm text-neutral-100">{r.name}{r.is_me && <span className="text-orange-400 text-[10px] font-bold"> · toi</span>}</span>
            <span className="font-extrabold text-orange-400 text-sm">{r.count}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const GoalRow = ({ icon: Icon, label, field, value, goal, done, remaining, perDay }) => {
  const pct = goal > 0 ? Math.min(100, Math.round((value / goal) * 100)) : 0;
  return (
    <div data-testid={`goal-${field}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-white flex items-center gap-1.5"><Icon className="w-4 h-4 text-orange-400" />{label}</span>
        <span className="text-xs font-bold">
          <span className={done ? 'text-green-400' : 'text-orange-400'}>{value}</span>
          <span className="text-neutral-500"> / {goal}</span>
        </span>
      </div>
      <Bar value={pct} className="h-3 bg-white/10" indicatorClassName={done ? 'bg-green-500' : 'bg-orange-500'} />
      {done ? (
        <p className="text-[11px] text-green-400 font-semibold mt-1.5 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />Objectif atteint cette semaine 🎯</p>
      ) : (
        <p className="text-[11px] text-neutral-400 mt-1.5 flex items-center gap-1" data-testid={`goal-pace-${field}`}>
          <Gauge className="w-3.5 h-3.5 text-orange-400" />Encore <span className="font-bold text-white">{remaining}</span> · vise <span className="font-bold text-orange-400">~{perDay}/jour</span>
        </p>
      )}
    </div>
  );
};

export default function Defis() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [goals, setGoals] = useState(null);
  const [goalsLb, setGoalsLb] = useState(null);
  const [goalsHist, setGoalsHist] = useState([]);
  const [editGoals, setEditGoals] = useState(false);
  const [goalInputs, setGoalInputs] = useState({ invitations_goal: 0, presentations_goal: 0 });
  const [recapOpen, setRecapOpen] = useState(false);
  const [goalsShareOpen, setGoalsShareOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingGoals, setDownloadingGoals] = useState(false);
  const prevAllDone = useRef(false);
  const recapRef = useRef(null);
  const goalsCardRef = useRef(null);

  const loadGoalsExtras = () => {
    routinesAPI.goalsLeaderboard().then(setGoalsLb).catch(() => {});
    routinesAPI.goalsHistory().then(setGoalsHist).catch(() => {});
  };

  useEffect(() => {
    routinesAPI.today().then((d) => { setData(d); prevAllDone.current = d.all_done; }).catch(() => {});
    routinesAPI.history().then(setHistory).catch(() => {});
    routinesAPI.goals().then((g) => { setGoals(g); loadGoalsExtras(); }).catch(() => {});
  }, []);

  const refreshGoals = () => {
    routinesAPI.goals().then(setGoals).catch(() => {});
    loadGoalsExtras();
  };

  const openEditGoals = () => {
    setGoalInputs({ invitations_goal: goals?.invitations_goal ?? 0, presentations_goal: goals?.presentations_goal ?? 0 });
    setEditGoals(true);
  };

  const saveGoals = async () => {
    try {
      const res = await routinesAPI.setGoalTargets({
        invitations_goal: parseInt(goalInputs.invitations_goal, 10) || 0,
        presentations_goal: parseInt(goalInputs.presentations_goal, 10) || 0,
      });
      setGoals(res);
      setEditGoals(false);
      loadGoalsExtras();
      toast.success('Objectifs mis à jour');
    } catch { toast.error('Échec de la mise à jour'); }
  };

  const apply = (res) => {
    setData(res);
    if (res.all_done && !prevAllDone.current) setRecapOpen(true);
    prevAllDone.current = res.all_done;
    routinesAPI.history().then(setHistory).catch(() => {});
    refreshGoals();
  };

  const toggleItem = async (routineKey, item) => {
    const current = data.checks[routineKey] || [];
    const next = current.includes(item) ? current.filter((i) => i !== item) : [...current, item];
    try {
      const res = await routinesAPI.set({ routine_key: routineKey, items: next, count: data.counts[routineKey] });
      apply(res);
    } catch { toast.error('Échec de la mise à jour'); }
  };

  const setCount = async (routineKey, value) => {
    const n = parseInt(value, 10);
    const res = await routinesAPI.set({ routine_key: routineKey, items: data.checks[routineKey] || [], count: Number.isNaN(n) ? 0 : n });
    setData(res);
    refreshGoals();
  };

  const setSubCount = async (routineKey, subKey, value) => {
    const n = parseInt(value, 10);
    const res = await routinesAPI.set({ routine_key: routineKey, items: data.checks[routineKey] || [], counts: { [subKey]: Number.isNaN(n) ? 0 : n } });
    setData(res);
    refreshGoals();
  };

  const captureDownload = async (ref, filename, setBusy) => {
    if (!ref.current) return;
    if (setBusy) setBusy(true);
    try {
      const canvas = await html2canvas(ref.current, { backgroundColor: null, scale: 2 });
      const link = document.createElement('a');
      link.download = filename;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Image enregistrée — partage-la ! 📲');
    } catch { toast.error("Échec de la génération de l'image"); }
    finally { if (setBusy) setBusy(false); }
  };

  const captureShare = async (ref, filename, text) => {
    if (!ref.current) return;
    try {
      const canvas = await html2canvas(ref.current, { backgroundColor: null, scale: 2 });
      const blob = await new Promise((res) => canvas.toBlob(res, 'image/png'));
      const file = new File([blob], filename, { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text, title: 'Body Fit Challenge 21' });
      } else {
        const link = document.createElement('a');
        link.download = file.name; link.href = canvas.toDataURL('image/png'); link.click();
        toast('Image enregistrée — partage-la sur WhatsApp ou Telegram 📲');
      }
    } catch { toast.error('Partage indisponible sur cet appareil'); }
  };

  const downloadRecap = () => captureDownload(recapRef, `bfc21-defis-${data.date}.png`, setDownloading);
  const shareNative = () => captureShare(recapRef, `bfc21-defis-${data.date}.png`, SHARE_TEXT);
  const downloadGoals = () => captureDownload(goalsCardRef, `bfc21-objectifs-${goals?.week_start}.png`, setDownloadingGoals);
  const shareGoalsNative = () => captureShare(goalsCardRef, `bfc21-objectifs-${goals?.week_start}.png`, GOALS_SHARE_TEXT);

  const shareWhatsApp = (text = SHARE_TEXT) => window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  const shareTelegram = (text = SHARE_TEXT) => window.open(`https://t.me/share/url?url=${encodeURIComponent(window.location.origin)}&text=${encodeURIComponent(text)}`, '_blank');

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;

  const { definitions, validated_keys, validated_count, total_routines, points_today, total_routine_points, profile } = data;
  const isDistrib = profile === 'distributeur';
  const summaryDefs = isDistrib ? definitions.filter((d) => d.group === 'distributeur') : definitions;
  const summaryValidated = isDistrib ? summaryDefs.filter((d) => validated_keys.includes(d.key)).length : validated_count;
  const summaryTotal = isDistrib ? summaryDefs.length : total_routines;
  const pct = summaryTotal ? Math.round((summaryValidated / summaryTotal) * 100) : 0;
  const fmtDay = (iso) => new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', timeZone: 'UTC' });
  const fmtFull = (iso) => new Date(iso + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <div className="space-y-6 animate-rise pb-6">
      <div>
        <h1 className="font-display text-4xl font-extrabold uppercase tracking-tight text-white flex items-center gap-2">
          <ListChecks className="w-8 h-8 text-orange-500" />Défis à relever
        </h1>
        <p className="text-neutral-400 text-sm mt-1">Tes routines quotidiennes — profil <span className="text-orange-400 font-bold">{PROFILE_LABEL[profile]}</span>. Coche au moins un élément pour valider une routine.</p>
        {isDistrib && (data.distrib_start_date || data.distrib_end_date) && (
          <div data-testid="distrib-challenge-dates" className="flex flex-wrap items-center gap-2 mt-3">
            {data.distrib_start_date && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-500/15 text-orange-300 text-xs font-semibold border border-orange-500/30">
                <CalendarDays className="w-3.5 h-3.5" />Démarrage : {fmtFull(data.distrib_start_date)}
              </span>
            )}
            {data.distrib_end_date && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-500/15 text-green-300 text-xs font-semibold border border-green-500/30">
                <Flag className="w-3.5 h-3.5" />Fin : {fmtFull(data.distrib_end_date)}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Day summary */}
      <Card className="p-5 rounded-2xl border-orange-500/30 bg-orange-500/10" data-testid="routines-summary">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-white">{isDistrib ? 'Routines Distributeur' : 'Routines du jour'}</span>
          <span className="text-sm font-extrabold text-orange-400" data-testid="routines-progress">{summaryValidated}/{summaryTotal}</span>
        </div>
        <Bar value={pct} className="h-3 bg-white/10" indicatorClassName="bg-orange-500" />
        <div className="flex items-center justify-between mt-3 text-xs">
          <span className="text-neutral-300">Points du jour : <span className="font-extrabold text-orange-400" data-testid="points-today">{points_today}</span></span>
          <span className="text-neutral-400 flex items-center gap-1"><Trophy className="w-3.5 h-3.5 text-orange-500" />Total routines : <span className="font-bold text-white">{total_routine_points}</span></span>
        </div>
        {profile === 'distributeur' && (
          <div className="flex items-center gap-2 mt-2">
            <Badge className="bg-sky-500/20 text-sky-300 border-sky-500/30 text-[10px]" data-testid="distrib-points">Distributeur : {data.distrib_points} pts</Badge>
            <Badge className="bg-orange-500/20 text-orange-300 border-orange-500/30 text-[10px]" data-testid="client-points">Client : {data.client_points} pts</Badge>
          </div>
        )}
      </Card>

      {/* Weekly distributor objectives — distributor sets own goals; progress computed from routines */}
      {profile === 'distributeur' && goals && (
        <Card className="p-5 rounded-2xl border-sky-500/30 bg-sky-500/5" data-testid="weekly-goals">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-display uppercase text-lg font-extrabold text-white flex items-center gap-2"><Target className="w-5 h-5 text-orange-500" />Objectifs atteints de la semaine</h3>
            {!editGoals && (
              <button onClick={openEditGoals} data-testid="edit-goals-btn" className="text-xs font-semibold text-sky-300 flex items-center gap-1 hover:text-sky-200">
                <Pencil className="w-3.5 h-3.5" />Modifier
              </button>
            )}
          </div>
          <p className="text-xs text-neutral-400 mb-4">Ta progression est calculée à partir des personnes saisies dans « Routines Distributeur ». Compteur remis à zéro chaque lundi.</p>
          {editGoals ? (
            <div className="space-y-3" data-testid="edit-goals-form">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[11px] text-neutral-400 flex items-center gap-1"><Users className="w-3.5 h-3.5 text-orange-400" />Objectif invitations</label>
                  <Input type="number" min="0" value={goalInputs.invitations_goal} onChange={(e) => setGoalInputs((g) => ({ ...g, invitations_goal: e.target.value }))} data-testid="goal-invitations-target" className="h-9 rounded-lg bg-neutral-800 border-neutral-700 text-white" />
                </div>
                <div className="space-y-1">
                  <label className="text-[11px] text-neutral-400 flex items-center gap-1"><Sparkles className="w-3.5 h-3.5 text-sky-400" />Objectif présentations</label>
                  <Input type="number" min="0" value={goalInputs.presentations_goal} onChange={(e) => setGoalInputs((g) => ({ ...g, presentations_goal: e.target.value }))} data-testid="goal-presentations-target" className="h-9 rounded-lg bg-neutral-800 border-neutral-700 text-white" />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={saveGoals} data-testid="save-goals-btn" className="flex-1 bg-orange-500 hover:bg-orange-600 rounded-full font-semibold">Enregistrer</Button>
                <Button onClick={() => setEditGoals(false)} variant="outline" className="rounded-full border-neutral-700 text-neutral-300">Annuler</Button>
              </div>
            </div>
          ) : (
            <>
              {goals.is_champion && (
                <div className="flex items-center gap-2 mb-4 p-3 rounded-xl bg-gradient-to-r from-amber-500/20 to-orange-500/10 border border-amber-500/30" data-testid="champion-banner">
                  <Crown className="w-6 h-6 text-amber-400 shrink-0" />
                  <div>
                    <p className="text-sm font-extrabold text-amber-300">Champion de la semaine 👑</p>
                    <p className="text-[11px] text-amber-200/80">Tes 2 objectifs sont atteints. Badge débloqué !</p>
                  </div>
                </div>
              )}
              <div className="space-y-5">
                <GoalRow icon={Users} label="Invitations" field="invitations" value={goals.invitations} goal={goals.invitations_goal} done={goals.invitations_done} remaining={goals.invitations_remaining} perDay={goals.invitations_per_day} />
                <GoalRow icon={Sparkles} label="Présentations" field="presentations" value={goals.presentations} goal={goals.presentations_goal} done={goals.presentations_done} remaining={goals.presentations_remaining} perDay={goals.presentations_per_day} />
              </div>
              <p className="text-[11px] text-neutral-500 mt-3 text-center">Plus que <span className="font-bold text-neutral-300">{goals.days_left} jour(s)</span> cette semaine</p>
              <Button onClick={() => setGoalsShareOpen(true)} data-testid="open-goals-share" variant="outline" className="w-full mt-3 rounded-full font-semibold border-orange-500/40 text-orange-400 hover:bg-orange-500/10">
                <Share2 className="w-4 h-4 mr-2" />Partager mes objectifs
              </Button>
            </>
          )}
        </Card>
      )}

      {/* Weekly goals leaderboards (two separate boards) */}
      {profile === 'distributeur' && goalsLb && (
        <Card className="p-5 rounded-2xl border-neutral-800 bg-neutral-900/60" data-testid="goals-leaderboard">
          <h3 className="font-display uppercase text-lg font-extrabold text-white flex items-center gap-2 mb-1"><Trophy className="w-5 h-5 text-orange-500" />Classement de la semaine</h3>
          <p className="text-xs text-neutral-400 mb-4">Les distributeurs les plus actifs cette semaine.</p>
          {goalsLb.champions?.length > 0 && (
            <div className="mb-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20" data-testid="champions-list">
              <p className="text-xs font-bold text-amber-300 flex items-center gap-1.5 mb-2"><Crown className="w-4 h-4" />Champions de la semaine</p>
              <div className="flex flex-wrap gap-2">
                {goalsLb.champions.map((c) => (
                  <div key={c.user_id} data-testid={`champion-${c.user_id}`} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold ${c.is_me ? 'bg-amber-500/25 text-amber-200 border border-amber-500/40' : 'bg-white/5 text-neutral-200'}`}>
                    <Crown className="w-3.5 h-3.5 text-amber-400" />{c.name}{c.is_me && <span className="text-amber-300"> · toi</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="grid gap-5 sm:grid-cols-2">
            <MiniBoard title="Top Invitations" icon={Users} field="invitations" rows={goalsLb.invitations} />
            <MiniBoard title="Top Présentations" icon={Sparkles} field="presentations" rows={goalsLb.presentations} />
          </div>
        </Card>
      )}

      {/* Weekly objectives history (6 weeks) */}
      {profile === 'distributeur' && goalsHist.length > 0 && (
        <Card className="p-5 rounded-2xl border-neutral-800 bg-neutral-900/60" data-testid="goals-history">
          <h3 className="font-bold text-white mb-3 text-sm flex items-center gap-2"><History className="w-4 h-4 text-orange-400" />Historique de mes objectifs (6 semaines)</h3>
          <div className="space-y-2.5">
            {goalsHist.map((w) => {
              const ip = w.invitations_goal > 0 ? Math.min(100, Math.round((w.invitations / w.invitations_goal) * 100)) : 0;
              const pp = w.presentations_goal > 0 ? Math.min(100, Math.round((w.presentations / w.presentations_goal) * 100)) : 0;
              return (
                <div key={w.week_start} data-testid={`goals-hist-${w.week_start}`} className="flex items-center gap-3">
                  <span className="text-[11px] text-neutral-400 w-12 shrink-0">sem. {fmtWeek(w.week_start)}</span>
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2"><Users className="w-3 h-3 text-orange-400 shrink-0" /><div className="flex-1"><Bar value={ip} className="h-1.5 bg-white/10" indicatorClassName={w.invitations >= w.invitations_goal && w.invitations_goal > 0 ? 'bg-green-500' : 'bg-orange-500'} /></div><span className="text-[10px] text-neutral-400 w-8 text-right">{w.invitations}/{w.invitations_goal}</span></div>
                    <div className="flex items-center gap-2"><Sparkles className="w-3 h-3 text-sky-400 shrink-0" /><div className="flex-1"><Bar value={pp} className="h-1.5 bg-white/10" indicatorClassName={w.presentations >= w.presentations_goal && w.presentations_goal > 0 ? 'bg-green-500' : 'bg-sky-500'} /></div><span className="text-[10px] text-neutral-400 w-8 text-right">{w.presentations}/{w.presentations_goal}</span></div>
                  </div>
                  {w.both_done ? <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" data-testid={`goals-hist-done-${w.week_start}`} /> : <Medal className="w-4 h-4 text-neutral-700 shrink-0" />}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* 7-day regularity strip */}
      {history.length > 0 && (
        <Card className="p-5 rounded-2xl border-neutral-800 bg-neutral-900/60" data-testid="routines-history">
          <h3 className="font-bold text-white mb-3 text-sm">Ta régularité (7 derniers jours)</h3>
          <div className="flex items-end justify-between gap-2">
            {history.map((h) => {
              const pct = h.total ? Math.round((h.validated_count / h.total) * 100) : 0;
              const d = new Date(h.date);
              const dow = DAYS_FR[d.getUTCDay()];
              return (
                <div key={h.date} className="flex-1 flex flex-col items-center gap-1.5" data-testid={`history-day-${h.date}`}>
                  <div className="w-full h-20 rounded-lg bg-neutral-800 flex items-end overflow-hidden">
                    <div className={`w-full rounded-lg transition-all ${h.all_done ? 'bg-green-500' : 'bg-orange-500'}`} style={{ height: `${Math.max(pct, 4)}%` }} />
                  </div>
                  <span className="text-[10px] text-neutral-400 font-semibold">{dow}</span>
                  <span className="text-[9px] text-neutral-500 font-medium" data-testid={`history-date-${h.date}`}>{fmtDay(h.date)}</span>
                  <span className={`text-[9px] font-bold flex items-center gap-0.5 ${h.all_done ? 'text-green-500' : 'text-neutral-400'}`} data-testid={`history-count-${h.date}`}>
                    {h.all_done && <CheckCircle2 className="w-3 h-3" />}{h.validated_count}/{h.total}
                  </span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Routine cards (grouped by profile) */}
      <div className="space-y-3">
        {definitions.map((r, idx) => {
          const checked = data.checks[r.key] || [];
          const isValid = validated_keys.includes(r.key);
          const showHeader = profile === 'distributeur' && (idx === 0 || definitions[idx - 1].group !== r.group);
          return (
          <React.Fragment key={r.key}>
            {showHeader && (
              <h2 className="font-display uppercase text-lg font-extrabold text-orange-400 pt-2" data-testid={`group-${r.group}`}>
                {r.group === 'distributeur' ? 'Routines Distributeur' : 'Routines Client'}
              </h2>
            )}
            <Card data-testid={`routine-${r.key}`} className={`p-4 rounded-2xl border ${isValid ? 'border-green-500/40 bg-green-500/5' : 'border-neutral-800 bg-neutral-900/60'}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-white">{r.label}</h3>
                {isValid && <Badge className="bg-green-600 text-white border-0 text-[10px]" data-testid={`routine-valid-${r.key}`}><CheckCircle2 className="w-3 h-3 mr-1" />Validée</Badge>}
              </div>
              <div className="flex flex-wrap gap-2">
                {r.items.map((item) => {
                  const on = checked.includes(item);
                  return (
                    <button
                      key={item}
                      onClick={() => toggleItem(r.key, item)}
                      data-testid={`item-${r.key}-${item}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors flex items-center gap-1 ${on ? 'bg-orange-500 border-orange-500 text-white' : 'bg-transparent border-neutral-700 text-neutral-300 hover:border-orange-500/60'}`}
                    >
                      {on && <CheckCircle2 className="w-3.5 h-3.5" />}{item}
                    </button>
                  );
                })}
              </div>
              {r.key === 'parler_inviter' && (
                <div className="flex items-center gap-2 mt-3" data-testid="count-parler_inviter-wrap">
                  <Users className="w-4 h-4 text-neutral-400" />
                  <span className="text-xs text-neutral-400">Personnes invitées aujourd'hui :</span>
                  <Input
                    type="number" min="0"
                    defaultValue={data.counts[r.key] ?? 0}
                    onBlur={(e) => setCount(r.key, e.target.value)}
                    data-testid={`count-${r.key}`}
                    className="w-20 h-8 rounded-lg bg-neutral-800 border-neutral-700 text-white text-sm"
                  />
                </div>
              )}
              {r.key === 'presenter_herbalife' && (
                <div className="mt-3 grid grid-cols-2 gap-3" data-testid="count-presentations-wrap">
                  <div>
                    <span className="text-[11px] text-neutral-400 block mb-1">Présentations produits</span>
                    <Input
                      type="number" min="0"
                      defaultValue={data.counts.presenter_produits ?? 0}
                      onBlur={(e) => setSubCount(r.key, 'presenter_produits', e.target.value)}
                      data-testid="count-presenter_produits"
                      className="w-full h-8 rounded-lg bg-neutral-800 border-neutral-700 text-white text-sm"
                    />
                  </div>
                  <div>
                    <span className="text-[11px] text-neutral-400 block mb-1">Présentations business</span>
                    <Input
                      type="number" min="0"
                      defaultValue={data.counts.presenter_business ?? 0}
                      onBlur={(e) => setSubCount(r.key, 'presenter_business', e.target.value)}
                      data-testid="count-presenter_business"
                      className="w-full h-8 rounded-lg bg-neutral-800 border-neutral-700 text-white text-sm"
                    />
                  </div>
                </div>
              )}
            </Card>
          </React.Fragment>
          );
        })}
      </div>

      {data.all_done && (
        <Button onClick={() => setRecapOpen(true)} data-testid="open-recap" className="w-full bg-orange-500 hover:bg-orange-600 rounded-full py-6 font-bold">
          <Sparkles className="w-5 h-5 mr-2" />Voir ma carte du jour à partager
        </Button>
      )}

      {/* Shareable recap */}
      <Dialog open={recapOpen} onOpenChange={setRecapOpen}>
        <DialogContent className="max-w-sm p-0 border-0 bg-transparent shadow-none" data-testid="routines-recap">
          <DialogTitle className="sr-only">Ma carte de routines du jour</DialogTitle>
          <DialogDescription className="sr-only">Carte récapitulative de tes routines validées à partager.</DialogDescription>
          <div ref={recapRef} className="rounded-3xl overflow-hidden bg-gradient-to-br from-[#0b0b0e] via-[#1a0d06] to-[#2a1205] p-6 text-white">
            <div className="flex items-center gap-2 mb-4">
              <img src="/bfc21-logo.png" alt="BFC21" className="w-10 h-10 rounded-full" />
              <div>
                <p className="font-display font-extrabold uppercase leading-none text-lg">Body Fit Challenge 21</p>
                <p className="text-[11px] text-orange-400">{PROFILE_LABEL[profile]} · {data.date}</p>
              </div>
            </div>
            <div className="text-center my-5">
              <div className="w-16 h-16 rounded-full bg-orange-500 mx-auto flex items-center justify-center mb-3 shadow-[0_0_30px_rgba(255,94,0,0.6)]">
                <CheckCircle2 className="w-9 h-9 text-white" />
              </div>
              <p className="font-display text-3xl font-extrabold uppercase">Journée complète !</p>
              <p className="text-sm text-neutral-300 mt-1">{(user?.name || '').split(' ')[0]}, tu as validé toutes tes {total_routines} routines 💪🔥</p>
            </div>
            <div className="space-y-1.5 bg-white/5 rounded-2xl p-4">
              {definitions.map((r) => (
                <div key={r.key} className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />
                  <span className="text-neutral-100">{r.label}</span>
                </div>
              ))}
            </div>
            <p className="text-center text-[11px] text-orange-300 mt-4 font-semibold">+{points_today} points routines aujourd'hui · Continue comme ça ! 🚀</p>
          </div>
          <div className="mt-3 px-1 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Button onClick={() => shareWhatsApp()} data-testid="share-whatsapp" className="bg-green-600 hover:bg-green-700 rounded-full font-semibold">
                <Share2 className="w-4 h-4 mr-2" />WhatsApp
              </Button>
              <Button onClick={() => shareTelegram()} data-testid="share-telegram" className="bg-sky-500 hover:bg-sky-600 rounded-full font-semibold">
                <Send className="w-4 h-4 mr-2" />Telegram
              </Button>
            </div>
            <Button onClick={shareNative} data-testid="share-native" variant="outline" className="w-full rounded-full font-semibold border-orange-500/40 text-orange-400 hover:bg-orange-500/10">
              <Share2 className="w-4 h-4 mr-2" />Partager l'image
            </Button>
            <Button onClick={downloadRecap} disabled={downloading} data-testid="download-recap" className="w-full bg-orange-500 hover:bg-orange-600 rounded-full font-semibold">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-2" />Télécharger l'image</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Shareable weekly objectives card */}
      {goals && (
        <Dialog open={goalsShareOpen} onOpenChange={setGoalsShareOpen}>
          <DialogContent className="max-w-sm p-0 border-0 bg-transparent shadow-none" data-testid="goals-recap">
            <DialogTitle className="sr-only">Partager mes objectifs de la semaine</DialogTitle>
            <DialogDescription className="sr-only">Carte récapitulative de tes objectifs hebdomadaires à partager.</DialogDescription>
            <div ref={goalsCardRef} className="rounded-3xl overflow-hidden bg-gradient-to-br from-[#0b0b0e] via-[#061a1a] to-[#052a24] p-6 text-white">
              <div className="flex items-center gap-2 mb-4">
                <img src="/bfc21-logo.png" alt="BFC21" className="w-10 h-10 rounded-full" />
                <div>
                  <p className="font-display font-extrabold uppercase leading-none text-lg">Body Fit Challenge 21</p>
                  <p className="text-[11px] text-orange-400">Objectifs de la semaine</p>
                </div>
              </div>
              <div className="text-center my-5">
                <div className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-3 ${goals.invitations_done && goals.presentations_done ? 'bg-green-500 shadow-[0_0_30px_rgba(34,197,94,0.6)]' : 'bg-orange-500 shadow-[0_0_30px_rgba(255,94,0,0.6)]'}`}>
                  {goals.invitations_done && goals.presentations_done ? <Trophy className="w-9 h-9 text-white" /> : <Target className="w-9 h-9 text-white" />}
                </div>
                <p className="font-display text-3xl font-extrabold uppercase leading-none">{goals.invitations_done && goals.presentations_done ? 'Objectifs atteints !' : 'En route vers l\u2019objectif'}</p>
                <p className="text-orange-400 font-extrabold text-lg mt-2" data-testid="goals-recap-name">{user?.name || 'Distributeur'}</p>
              </div>
              <div className="space-y-3 bg-white/5 rounded-2xl p-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2"><Users className="w-4 h-4 text-orange-400" />Invitations</span>
                  <span className="font-extrabold flex items-center gap-1.5">{goals.invitations}/{goals.invitations_goal}{goals.invitations_done && <CheckCircle2 className="w-4 h-4 text-green-400" />}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-sky-400" />Présentations</span>
                  <span className="font-extrabold flex items-center gap-1.5">{goals.presentations}/{goals.presentations_goal}{goals.presentations_done && <CheckCircle2 className="w-4 h-4 text-green-400" />}</span>
                </div>
              </div>
              <p className="text-center text-[11px] text-orange-300 mt-4 font-semibold">#BodyFitChallenge21 · On lâche rien ! 🚀</p>
            </div>
            <div className="mt-3 px-1 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={() => shareWhatsApp(GOALS_SHARE_TEXT)} data-testid="goals-share-whatsapp" className="bg-green-600 hover:bg-green-700 rounded-full font-semibold">
                  <Share2 className="w-4 h-4 mr-2" />WhatsApp
                </Button>
                <Button onClick={() => shareTelegram(GOALS_SHARE_TEXT)} data-testid="goals-share-telegram" className="bg-sky-500 hover:bg-sky-600 rounded-full font-semibold">
                  <Send className="w-4 h-4 mr-2" />Telegram
                </Button>
              </div>
              <Button onClick={shareGoalsNative} data-testid="goals-share-native" variant="outline" className="w-full rounded-full font-semibold border-orange-500/40 text-orange-400 hover:bg-orange-500/10">
                <Share2 className="w-4 h-4 mr-2" />Partager l'image
              </Button>
              <Button onClick={downloadGoals} disabled={downloadingGoals} data-testid="goals-download" className="w-full bg-orange-500 hover:bg-orange-600 rounded-full font-semibold">
                {downloadingGoals ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-2" />Télécharger l'image</>}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
